import { useMutation, useQuery } from "@apollo/client";
import {
  OfficeBuildingIcon,
  PencilIcon,
  PlusCircleIcon,
  PlusIcon,
  TrashIcon,
  ViewGridAddIcon,
} from "@heroicons/react/outline";
import {
  groupBy,
  isArray,
  mapValues,
  max,
  omit,
  reduce,
  sortBy,
  uniq,
} from "lodash";
import {
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { Prompt, useParams } from "react-router-dom";
import {
  CareerTrackRoleInput,
  CareerTrackRoleType,
  CompetencyAppliesTo,
  CompetencyCriteriaInput,
  CompetencyCriteriaUniqueness,
  CompetencyNode,
  DeleteOrgWideCareerTrackMutation,
  DeleteOrgWideCareerTrackMutationVariables,
  GetOrganizationCareerTrackQuery,
  GetOrganizationCareerTrackQueryVariables,
  GetOrganizationCompetenciesQuery,
  GetOrganizationCompetenciesQueryVariables,
  OrganizationCareerTrackFragmentFragment,
  OrganizationCompetencyCriteriaFragment,
  OrganizationCompetencyFragment,
  RoleResponsibilityInput,
  SaveOrgWideCareerTrackMutation,
  SaveOrgWideCareerTrackMutationVariables,
  SaveOrgWideCompetencyMutation,
  SaveOrgWideCompetencyMutationVariables,
  SavePersonCareerCriteriaMutation,
  SavePersonCareerCriteriaMutationVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";
import { v4 as uuidv4 } from "uuid";

import TeamEditDialog from "@apps/org-settings/components/team-edit-dialog";
import createOrUpdateCareerTrackMutation from "@apps/org-settings/graphql/create-or-update-career-track-mutation";
import createOrUpdateOrgWideCompetencyMutation from "@apps/org-settings/graphql/create-or-update-org-wide-competency-mutation";
import deleteOrgWideCareerTrackMutation from "@apps/org-settings/graphql/delete-org-wide-career-track-mutation";
import getOrganizationCareerTrackQuery from "@apps/org-settings/graphql/get-organization-career-track-query";
import getOrganizationCompetenciesQuery from "@apps/org-settings/graphql/get-organization-competencies-query";
import getPersonCareerQuery from "@apps/org-settings/graphql/get-person-career-query";
import savePersonCareerCriteriaMutation from "@apps/org-settings/graphql/save-person-career-criteria-mutation";
import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericButtonProps,
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import {
  UserComboboxOptionType,
  UserComboboxTeamOption,
} from "@components/user-combobox/user-combobox-list";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import SettingsPageHeader from "../../settings-page-header";
import AddButton from "../add-button";
import AutosaveStatus from "../autosave-status";
import CareerTrackViewSwitcher from "../career-track-view-switcher";
import CompetencyFormModal from "../competency-form-modal";
import CompetencySharingChip from "../competency-sharing-chip";
import { sectionHeaderToneClassName } from "../competency-sharing-tone";
import CriteriaEditor from "../criteria-editor";
import EditModeToggle from "../edit-mode-toggle";
import {
  CompetencyScopeSection,
  addLevelColClassName,
  bodyRowLineClassName,
  careerTrackOverviewPath,
  careerTrackPath,
  carrySavedIds,
  criteriaUuid,
  describeRoleHolders,
  doesCompetencyApplyToCareerTrack,
  emptyRoleColWidth,
  getCompetencyScopeSection,
  getDefaultRoleTitle,
  getRolesAndCompetenciesBreadcrumbs,
  keepBlankCriteriaAfterSave,
  leaveBlankUnsaved,
  mergeSavedWithLocal,
  newCareerTrackPath,
  removedIds,
  roleColWidth,
  roleLevelIterator,
  roleUuid,
  savedCriteriaByCompetencyId,
  stickyCellBottomLineClassName,
  stickyColWidth,
  stickyHeaderBorderClassName,
  withSavedId,
} from "../helpers";
import ScopeSectionIcon from "../scope-section-icon";
import StickyColumnShadow from "../sticky-column-shadow";
import {
  CompetencyCriteriaInputWithUUID,
  CriteriaByCompetencyIdMap,
} from "../types";
import useAutosave from "../use-autosave";
import usePendingDeletes from "../use-pending-deletes";
import useRolesEditMode from "../use-roles-edit-mode";
import useRolesRowOrder from "../use-roles-row-order";
import CareerTrackCompetencyRow from "./career-track-competency-row";
import CareerTrackDetailsModal from "./career-track-details-modal";
import EmptyCriteriaCell from "./empty-criteria-cell";
import RoleHeading from "./role-heading";
import TitleRow from "./title-row";

type CareerTrackFormResponsibility = RoleResponsibilityInput & {
  uuid: string;
};

export type CareerTrackFormRole = Omit<
  CareerTrackRoleInput,
  "responsibilities" | "assignToUsers"
> & {
  uuid: string;
  criteriaByCompetencyId: CriteriaByCompetencyIdMap;
  responsibilities: CareerTrackFormResponsibility[];
  assignToUsers: BasicUser[];
};

// A blank role for a level. Its empty title means "use the default name"
// (see getDefaultRoleTitle), which is filled in when the track is saved.
const createDefaultRole = (level: number): CareerTrackFormRole => ({
  uuid: uuidv4(),
  level,
  title: "",
  roleType: CareerTrackRoleType.Ic,
  criteriaByCompetencyId: {},
  responsibilities: [],
  assignToUsers: [],
});

const AddCompetencyDropdown = ({
  options,
  header,
  onAddCompetency,
  onCreateCompetency,
  children,
}: {
  options: ComboboxGenericOption<number>[];
  // Says what the competency is being added to.
  header?: string;
  onAddCompetency: (competencyId: number) => void;
  // Left out until the track is saved: there's no track yet to scope a new
  // competency to, so it would be made company-wide.
  onCreateCompetency?: (title: string) => void;
  children: ReactElement<ComboboxGenericButtonProps>;
}) => {
  const label = useLabel();
  // Nothing left to pick: the dropdown would be empty, so go straight to
  // creating one.
  if (options.length === 0) {
    return onCreateCompetency ? (
      <Button {...children.props} onClick={() => onCreateCompetency("")} />
    ) : (
      <Button
        {...children.props}
        disabled
        tooltip={`Create a ${label("competency")} once this track is saved`}
      />
    );
  }
  return (
    <ComboboxGeneric
      value={null}
      header={header}
      options={options}
      createable={!!onCreateCompetency}
      createLabel={`Create new ${label("competency")}`}
      onClickCreateNew={
        onCreateCompetency ? () => onCreateCompetency("") : undefined
      }
      onAddNewValue={onCreateCompetency}
      onChangeValue={(opt) => onAddCompetency(opt.value)}
    >
      {children}
    </ComboboxGeneric>
  );
};

const CareerTrackForm = ({ organizationId }: { organizationId: number }) => {
  const params = useParams<{ careerTrackId?: string }>();
  // The id a brand new track was just given: its URL changes from "new" to
  // that id, which isn't switching tracks. Every other change of the URL is,
  // and bumps the key so the editor remounts with nothing carried over.
  const createdIdRef = useRef<string | null>(null);
  const routeParam = params.careerTrackId ?? "new";
  const previousParamRef = useRef(routeParam);
  const mountKeyRef = useRef(0);
  if (routeParam !== previousParamRef.current) {
    const isNewTrackGettingItsId =
      previousParamRef.current === "new" && routeParam === createdIdRef.current;
    if (!isNewTrackGettingItsId) {
      mountKeyRef.current += 1;
      // Only a save of the editor now showing can give "new" its id.
      createdIdRef.current = null;
    }
    previousParamRef.current = routeParam;
  }
  const mountKey = mountKeyRef.current;
  return (
    <CareerTrackFormContent
      key={mountKey}
      organizationId={organizationId}
      onTrackCreated={(careerTrackId) => {
        // A save that finishes after its editor was left doesn't count.
        if (mountKeyRef.current === mountKey) {
          createdIdRef.current = String(careerTrackId);
        }
      }}
    />
  );
};

// One person's view of their track: their level's column only, plus the
// criteria written for them alone.
export type CareerTrackPersonFocus = {
  id: number;
  name: string;
  avatar?: string | null;
  level: number;
  criteria: ReadonlyArray<{ id: number; content: string }>;
};

export const CareerTrackFormContent = ({
  organizationId,
  onTrackCreated,
  careerTrackIdProp,
  person,
  roleChooser,
}: {
  organizationId: number;
  onTrackCreated: (careerTrackId: number) => void;
  // The track to show when it isn't the one in the URL (a person's view).
  careerTrackIdProp?: number;
  person?: CareerTrackPersonFocus;
  // A row under the toolbar for picking which of a person's roles to show,
  // when they hold more than one. It gets this form's "save now", to finish
  // saving before another role's form opens.
  roleChooser?: (saveFirst: () => Promise<boolean>) => ReactNode;
}) => {
  const label = useLabel();
  const link = useLink();
  const tableRef = useRef<HTMLDivElement>(null);

  const params = useParams<{ careerTrackId: string }>();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isTrackDeleted, setIsTrackDeleted] = useState(false);
  // Counts edits, so a save's response can tell whether the form was edited
  // while it was in flight (and must not be rebuilt from that response).
  const editCountRef = useRef(0);
  const markDirty = useCallback(() => {
    editCountRef.current += 1;
    setIsFormDirty(true);
  }, []);
  // A person's own criteria save on their own: editing only those mustn't
  // resend the whole track, which could undo someone else's change to it.
  const markPersonCriteriaDirty = useCallback(() => setIsFormDirty(true), []);
  // The track's edit count when its last save went through.
  const savedTrackEditCountRef = useRef(0);
  // What the save in flight sent, and the edit count when it started.
  const sentRef = useRef<{ editCount: number; roles: CareerTrackFormRole[] }>({
    editCount: 0,
    roles: [],
  });
  // The editor can be left while a save is in flight; its response then
  // mustn't navigate back to it.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const [levelCount, setLevelCount] = useState(1);
  const [isCreatingDepartment, setIsCreatingDepartment] = useState(false);
  // Creating a competency from a section starts it with that section's reach.
  const [creatingCompetency, setCreatingCompetency] = useState<{
    title: string;
    scope: CompetencyScopeSection;
  } | null>(null);

  const careerTrackId = useMemo(() => {
    if (careerTrackIdProp) {
      return careerTrackIdProp;
    }
    if (params.careerTrackId?.match(/\d+/)) {
      return parseInt(params.careerTrackId);
    }
    return null;
  }, [careerTrackIdProp, params]);
  const isNew = careerTrackId === null;
  // A track that doesn't exist yet can only be edited.
  const { isEditing: isEditMode, setIsEditing } = useRolesEditMode();
  const { isRowOrderReversed, toggleRowOrder } = useRolesRowOrder();
  // The table's blocks and sections, top to bottom, or bottom to top.
  const orderRows = <T,>(items: T[]) =>
    isRowOrderReversed ? [...items].reverse() : items;
  const isEditing = isNew || isEditMode;
  // Starting a new track switches edit mode on, so it stays on once the
  // first save gives the track an id (and it stops being "new").
  useEffect(() => {
    if (isNew) {
      setIsEditing(true);
    }
  }, [isNew, setIsEditing]);
  // The person's own criteria: what's on screen, with unsaved edits. Null
  // until edited, so what's saved shows as is.
  const [personCriteriaEdits, setPersonCriteriaEdits] = useState<
    CompetencyCriteriaInputWithUUID[] | null
  >(null);
  const [savePersonCriteria] = useMutation<
    SavePersonCareerCriteriaMutation,
    SavePersonCareerCriteriaMutationVariables
  >(savePersonCareerCriteriaMutation, {
    // Each of their roles opens a new form from the page's copy, so that copy
    // has to follow the saves.
    refetchQueries: [getPersonCareerQuery],
  });
  const personCriteria = useMemo(
    () =>
      personCriteriaEdits ??
      (person?.criteria ?? []).map((item) => ({
        id: item.id,
        content: item.content,
        uuid: criteriaUuid(item.id),
      })),
    [person, personCriteriaEdits],
  );
  // New tracks open the details modal so a name gets entered first.
  const [isEditingDetails, setIsEditingDetails] = useState(isNew);

  const [careerTrackData, setCareerTrackData] = useState<
    Omit<SaveOrgWideCareerTrackMutationVariables, "roles">
  >({
    title: "",
    organizationId,
  });
  const [associatedTeam, setAssociatedTeam] =
    useState<UserComboboxTeamOption | null>(null);
  // Unsaved edits to shared ("same across roles") criteria, by competency id:
  // the competency's full criteria list across all levels.
  const [sharedCriteriaEdits, setSharedCriteriaEdits] = useState<
    Record<number, CompetencyCriteriaInputWithUUID[]>
  >({});
  const [roles, setRoles] = useState<CareerTrackFormRole[]>(
    isNew ? [createDefaultRole(1)] : [],
  );
  // What the bin buttons took out, for the next save to send: a save deletes
  // only the ids it names. Lists, by the role's key so a role not saved yet
  // has them too: "roles", "criteria-<role>-<competency id>",
  // "responsibilities-<role>", "holders-<role>" (user ids), "shared-<id>" (a
  // competency's shared criteria) and "person".
  const pendingDeletes = usePendingDeletes();
  // Who holds each saved role on the server. A save ends only these, and
  // assigns only the others: someone ended elsewhere isn't put back.
  const savedHolderIdsRef = useRef(new Map<number, number[]>());
  // Whether a track save is running: one that adds a holder makes them a
  // saved one when it lands.
  const isTrackSaveInFlightRef = useRef(false);
  // The track's name as last saved: a save sends it only when it changed.
  const savedTitleRef = useRef<string | null>(null);
  const rememberSavedHolders = (
    careerTrack: OrganizationCareerTrackFragmentFragment,
  ) => {
    savedTitleRef.current = careerTrack.title;
    savedHolderIdsRef.current = new Map(
      assertEdgesNonNull(careerTrack.roles).map((role) => [
        role.id,
        assertEdgesNonNull(role.assignedUsers).map(({ id }) => id),
      ]),
    );
  };

  const {
    data: competenciesData,
    loading: isCompetenciesLoading,
    refetch: refetchCompetencies,
  } = useQuery<
    GetOrganizationCompetenciesQuery,
    GetOrganizationCompetenciesQueryVariables
  >(getOrganizationCompetenciesQuery, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
    variables: {
      organizationId,
    },
  });

  const updateCareerTrackFormState = (
    careerTrack: OrganizationCareerTrackFragmentFragment,
    // A save that came back with no roles is the last role being deleted, so
    // the track stays empty. Loading an empty track instead starts it with a
    // default role.
    { fromSave = false }: { fromSave?: boolean } = {},
  ) => {
    const roles = assertEdgesNonNull(careerTrack.roles);
    // The saved track doesn't carry the competencies added by hand in this
    // form, so keep them: without it they'd drop out of the table after a save
    // until the competency list is refetched.
    setCareerTrackData((prev) => ({
      ...careerTrack,
      additionalCompetencies: prev.additionalCompetencies,
    }));
    if (roles.length === 0) {
      setRoles(fromSave ? [] : [createDefaultRole(1)]);
    } else {
      setRoles((previousRoles) =>
        roles.map((role) => {
          const previousRole = previousRoles.find(
            (previous) => previous.level === role.level,
          );
          const roleCompetencies = assertEdgesNonNull(role.competencies);
          // Keep the keys the responsibilities already have (so the editor
          // being typed in isn't torn down) and whatever isn't saved yet.
          const roleResponsibilities: CareerTrackFormResponsibility[] =
            mergeSavedWithLocal(
              assertEdgesNonNull(role.responsibilities).map(
                (responsibility) => ({
                  ...responsibility,
                  uuid: `responsibility-${responsibility.id}`,
                }),
              ),
              previousRole?.responsibilities ?? [],
              pendingDeletes.savedIds(),
            );
          const criteriaByCompetencyId: CriteriaByCompetencyIdMap =
            roleCompetencies.reduce((acc, competency) => {
              const criteria = mergeSavedWithLocal(
                assertEdgesNonNull(competency.criteria).map(
                  ({ id, content, level }) => ({
                    id,
                    content,
                    level,
                    uuid: criteriaUuid(id),
                  }),
                ),
                previousRole?.criteriaByCompetencyId[competency.id] ?? [],
                pendingDeletes.savedIds(),
              );
              return {
                ...acc,
                [competency.id]: [...(acc[competency.id] ?? []), ...criteria],
              };
            }, {} as CriteriaByCompetencyIdMap);
          // Competencies the saved track doesn't list (one added by hand, say)
          // still have their unsaved criteria on screen.
          Object.entries(previousRole?.criteriaByCompetencyId ?? {}).forEach(
            ([competencyId, criteria]) => {
              if (criteriaByCompetencyId[Number(competencyId)]) {
                return;
              }
              const unsaved = criteria.filter(
                (item) => !pendingDeletes.savedId(item),
              );
              if (unsaved.length > 0) {
                criteriaByCompetencyId[Number(competencyId)] = unsaved;
              }
            },
          );
          return {
            level: role.level,
            title: role.title,
            roleType: role.roleType,
            id: role.id,
            // A level keeps its key once saved, for the same reason: the role
            // name input stays mounted while it's being typed in.
            uuid: previousRole?.uuid ?? roleUuid(role.id),
            criteriaByCompetencyId,
            responsibilities: roleResponsibilities,
            assignToUsers: assertEdgesNonNull(role.assignedUsers),
          };
        }),
      );
    }
    setLevelCount(
      roles.length > 0 ? (max(roles.map((role) => role.level)) ?? 1) : 1,
    );
    setAssociatedTeam(
      careerTrack.team
        ? {
            id: assertNonNull(careerTrack?.team?.id),
            title: assertNonNull(careerTrack?.team?.title),
            type: UserComboboxOptionType.TEAM,
          }
        : null,
    );
  };

  // A save's response when the form was edited while it was in flight: what's
  // on screen stays (a deleted role stays deleted, retyped text stays), and
  // only the ids that save gave the roles, criteria and responsibilities it
  // created are carried over, so the next save updates them.
  const carrySavedIdsIntoRoles = () => {
    const savedIds = pendingDeletes.savedIds();
    setRoles((currentRoles) =>
      carrySavedIds(currentRoles, savedIds).map((role) => ({
        ...role,
        responsibilities: carrySavedIds(role.responsibilities, savedIds),
        criteriaByCompetencyId: mapValues(
          role.criteriaByCompetencyId,
          (criteria) => carrySavedIds(criteria, savedIds),
        ),
      })),
    );
  };

  // The id this editor gave a brand new track. Its state is already what was
  // just saved, so it doesn't fetch the track back: that would blank the
  // editor (and whatever is being typed) the moment the URL gains the id.
  const createdTrackIdRef = useRef<number | null>(null);
  const isShowingCreatedTrack =
    careerTrackId !== null && careerTrackId === createdTrackIdRef.current;

  const { data, loading: isCareerTrackLoading } = useQuery<
    GetOrganizationCareerTrackQuery,
    GetOrganizationCareerTrackQueryVariables
  >(getOrganizationCareerTrackQuery, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
    variables: {
      careerTrackId: careerTrackId ?? 0,
    },
    skip: isNew || isShowingCreatedTrack,
    onCompleted: (data) => {
      const careerTrack = data.careerTrack;
      if (careerTrack) {
        rememberSavedHolders(careerTrack);
        updateCareerTrackFormState(careerTrack);
      }
    },
  });

  const [saveCareerTrack] = useMutation<
    SaveOrgWideCareerTrackMutation,
    SaveOrgWideCareerTrackMutationVariables
  >(createOrUpdateCareerTrackMutation, {
    // A failed save rejects, so autosave shows the error and offers Retry.
    onCompleted: (response) => {
      // First: a save built before the page catches up (leaving the editor,
      // say) then updates what this one created rather than creating it again.
      pendingDeletes.learnCreatedIds(
        response.createOrUpdateCareerTrack?.createdIds,
      );
      const careerTrack = response.createOrUpdateCareerTrack?.careerTrack;
      if (careerTrack) {
        rememberSavedHolders(careerTrack);
        const sent = sentRef.current;
        if (editCountRef.current === sent.editCount) {
          updateCareerTrackFormState(careerTrack, { fromSave: true });
          setIsFormDirty(false);
        } else {
          carrySavedIdsIntoRoles();
        }
        // Only the save that created it: one that ran after the editor was
        // left still sees the page as new.
        if (isNew && createdTrackIdRef.current === null) {
          successNotificationVar({
            title: `Career track created`,
            timeout: 2000,
          });
          // Swap the "new" URL for the track's own, in place: the editor keeps
          // its state and every later autosave updates this track.
          createdTrackIdRef.current = careerTrack.id;
          onTrackCreated(careerTrack.id);
          if (isMountedRef.current) {
            link.replace(
              `/settings/organization/${organizationId}/career-track/edit/${careerTrack.id}`,
            );
          }
        }
      }
    },
  });

  const [saveCompetency] = useMutation<
    SaveOrgWideCompetencyMutation,
    SaveOrgWideCompetencyMutationVariables
  >(createOrUpdateOrgWideCompetencyMutation);

  const fullCompetenciesList: (Pick<
    CompetencyNode,
    | "id"
    | "title"
    | "criteriaUniqueness"
    | "appliesTo"
    | "appliesToIcRoles"
    | "appliesToManagementRoles"
  > &
    Pick<
      OrganizationCompetencyFragment,
      "appliesToTeams" | "appliesToCareerTracks"
    > &
    OrganizationCompetencyCriteriaFragment)[] = useMemo(() => {
    const competencyList = competenciesData
      ? assertEdgesNonNull(competenciesData?.competencies).filter(
          (competency) =>
            doesCompetencyApplyToCareerTrack(
              competency,
              roles,
              careerTrackData,
              careerTrackId,
              associatedTeam,
            ),
        )
      : [];
    return competencyList.map((competency) => {
      return {
        ...competency,
        criteria: {
          __typename: "CompetencyCriteriaConnection",
          edges: competency.criteria.edges.map((criteriaNode) => {
            return {
              ...criteriaNode,
              __typename: "CompetencyCriteriaEdge",
              node: criteriaNode
                ? {
                    __typename: "CompetencyCriteriaNode",
                    id: assertNonNull(criteriaNode.node?.id),
                    content: assertNonNull(criteriaNode.node?.content),
                    level: assertNonNull(criteriaNode.node?.level),
                    uuid: criteriaUuid(criteriaNode.node?.id),
                  }
                : null,
            };
          }),
        },
      };
    });
  }, [associatedTeam, careerTrackData, careerTrackId, competenciesData, roles]);

  // Shared criteria belong to the competency, not to this track, so they can
  // sit on levels this track has no role for. Those levels are still part of
  // the ladder: show them, with an empty role column to fill in.
  const displayLevelCount = useMemo(() => {
    const sharedCriteriaLevels = fullCompetenciesList.flatMap((competency) =>
      competency.criteriaUniqueness ===
      CompetencyCriteriaUniqueness.SameAcrossRoles
        ? (
            sharedCriteriaEdits[competency.id] ??
            assertEdgesNonNull(competency.criteria)
          ).map((criteriaItem) => criteriaItem.level ?? 1)
        : [],
    );
    return Math.max(levelCount, ...sharedCriteriaLevels, 1);
  }, [fullCompetenciesList, sharedCriteriaEdits, levelCount]);

  const otherCompetencyOptions = useMemo(() => {
    const excludedIds = fullCompetenciesList.map(({ id }) => id);
    const others = competenciesData
      ? assertEdgesNonNull(competenciesData?.competencies).filter(
          (competency) =>
            !excludedIds.includes(competency.id) &&
            // Narrowed to a role type alone, it reaches that type on every
            // track: linking it to this one would take it from the others,
            // so the server refuses it.
            !(
              competency.appliesTo === CompetencyAppliesTo.SomeRoles &&
              (competency.appliesToManagementRoles ||
                competency.appliesToIcRoles) &&
              assertEdgesNonNull(competency.appliesToTeams).length === 0 &&
              assertEdgesNonNull(competency.appliesToCareerTracks).length === 0
            ),
        )
      : [];
    return others.map((competency) => ({
      label: competency.title,
      value: competency.id,
    }));
  }, [competenciesData, fullCompetenciesList]);

  const handleSaveCareerTrack = useCallback(async () => {
    setIsFormDirty(false);
    const trackEditCount = editCountRef.current;
    sentRef.current = { editCount: trackEditCount, roles };
    // Read with the roles above: anything deleted from here on is the next
    // save's.
    const deletes = pendingDeletes.snapshot();
    const savedHolders = savedHolderIdsRef.current;
    // Ids saves gave what they created: this save may be built from a render
    // that hasn't caught up with them (see withSavedId).
    const savedIds = pendingDeletes.savedIds();
    const pendingIn = (list: string) => deletes[list] ?? [];
    const dropSent = (list: string) =>
      pendingDeletes.remove(list, pendingIn(list));
    // The person's own criteria are theirs, not the track's: saved on their
    // own, but with the track, so one "Saved" covers the page.
    if (person && personCriteriaEdits) {
      // Blank ones are the box still being typed in: a new one isn't saved
      // yet, and a saved one goes with its saved text, so it keeps its place.
      // Only the bin button deletes (deleteIds).
      const savedContentById = new Map(
        person.criteria.map((item) => [item.id, item.content]),
      );
      const sent = personCriteriaEdits
        .map((item) => withSavedId(item, savedIds))
        .flatMap((item) => {
          if (!isEmptyValue(item.content)) {
            return [item];
          }
          const savedContent = item.id && savedContentById.get(item.id);
          return savedContent ? [{ ...item, content: savedContent }] : [];
        });
      const response = await savePersonCriteria({
        variables: {
          organizationId,
          userId: person.id,
          criteria: sent.map((item) => ({
            ...(item.id ? { id: item.id } : { clientId: item.uuid }),
            content: item.content ?? "",
          })),
          deleteIds: pendingIn("person"),
        },
      });
      pendingDeletes.learnCreatedIds(
        response.data?.saveUserCareerCriteria?.createdIds,
      );
      dropSent("person");
      // Only the new ones' ids come from the save: anything typed, added or
      // deleted while it was in flight stands, for the next save to send.
      setPersonCriteriaEdits((current) =>
        carrySavedIds(current ?? personCriteriaEdits, savedIds),
      );
    }
    // Nothing on the track itself changed since its last save (only this
    // person's criteria did): leave the track as it is on the server.
    if (!isNew && trackEditCount === savedTrackEditCountRef.current) {
      return;
    }
    // Shared criteria edited in the table belong to their competencies, so
    // save those first (see leaveBlankUnsaved).
    const editedCompetencies = Object.entries(sharedCriteriaEdits);
    if (editedCompetencies.length > 0) {
      const results = await Promise.allSettled(
        editedCompetencies.map(async ([competencyId, criteria]) => {
          const response = await saveCompetency({
            variables: {
              competencyId: Number(competencyId),
              criteria: leaveBlankUnsaved(
                criteria.map((item) => withSavedId(item, savedIds)),
              ).map((item) => omit(item, "uuid", "__typename")),
              deleteCriteriaIds: pendingIn(`shared-${competencyId}`),
            },
          });
          // Before the refetch, which can fail: the retry then updates what
          // this created rather than creating it again.
          pendingDeletes.learnCreatedIds(
            response.data?.createOrUpdateCompetency?.createdIds,
          );
          dropSent(`shared-${competencyId}`);
        }),
      );
      // The ones that saved get their new ids; one that failed keeps its list
      // as it is, for the retry.
      const savedEdits = Object.fromEntries(
        editedCompetencies.filter(
          (_edit, index) => results[index].status === "fulfilled",
        ),
      );
      const refreshed = await refetchCompetencies();
      const savedCriteria = savedCriteriaByCompetencyId(refreshed.data);
      setSharedCriteriaEdits((prev) =>
        keepBlankCriteriaAfterSave(
          prev,
          savedEdits,
          savedCriteria,
          isEmptyValue,
          pendingDeletes.savedIds(),
        ),
      );
      // A failure rejects, which autosave shows as an error with Retry.
      const failed = results.find((result) => result.status === "rejected");
      if (failed) {
        throw failed.reason;
      }
    }
    // Each delete list as the track save sends it, to drop once it succeeds.
    const sentDeletes: [string, ReadonlyArray<number>][] = [];
    // Taken off while a save was adding them, and that save has landed
    // without making them a holder: there's nothing to end.
    roles.forEach((role) => {
      const roleId = pendingDeletes.savedId(role);
      const held = (roleId && savedHolders.get(roleId)) || [];
      const list = `holders-${role.uuid}`;
      pendingDeletes.remove(
        list,
        pendingIn(list).filter((id) => !held.includes(id)),
      );
    });
    const send = (list: string, ids = pendingIn(list)) => {
      sentDeletes.push([list, ids]);
      return ids;
    };
    // Waited on, so "Saved" only shows once it has, and the next save only
    // starts after it: a brand new track is created once, then updated.
    const response = await saveCareerTrack({
      variables: {
        ...careerTrackData,
        // Only when renamed here: left out, the saved name stays.
        title:
          careerTrackData.title !== savedTitleRef.current
            ? careerTrackData.title
            : undefined,
        deleteRoleIds: send("roles"),
        // Ascending level order, so when levels shift down after a delete the
        // backend never moves a role onto a level another role still holds.
        roles: sortBy(roles, (role) => role.level).map((item) => {
          const roleCriteria = reduce<
            CriteriaByCompetencyIdMap,
            CompetencyCriteriaInput[]
          >(
            item.criteriaByCompetencyId,
            (acc, criteria, criteriaId) => {
              const competency = fullCompetenciesList.find(
                (competency) => competency.id === parseInt(criteriaId),
              );
              if (
                !competency ||
                competency.criteriaUniqueness ===
                  CompetencyCriteriaUniqueness.SameAcrossRoles
              ) {
                return acc;
              }

              const newCriteria: CompetencyCriteriaInput[] = leaveBlankUnsaved(
                criteria.map((criteriaItem) =>
                  withSavedId(criteriaItem, savedIds),
                ),
              ).map((criteriaItem) => ({
                ...omit(criteriaItem, "uuid"),
                competencyId: competency.id,
              }));

              return [...acc, ...newCriteria];
            },
            [],
          );
          const criteriaLists = Object.keys(deletes).filter((list) =>
            list.startsWith(`criteria-${item.uuid}-`),
          );
          const holdersList = `holders-${item.uuid}`;
          const roleId = pendingDeletes.savedId(item);
          const savedHolderIds = (roleId && savedHolders.get(roleId)) || [];
          return {
            ...omit(
              withSavedId(item, savedIds),
              "uuid",
              "criteriaByCompetencyId",
            ),
            title:
              item.title?.trim() ||
              getDefaultRoleTitle(careerTrackData.title, item.level ?? 1),
            // An emptied editor still holds an empty Tiptap document, which
            // is a non-empty string: leaveBlankUnsaved treats it as blank.
            responsibilities: leaveBlankUnsaved(
              item.responsibilities.map((responsibility) =>
                withSavedId(responsibility, savedIds),
              ),
            ).map((responsibility) =>
              omit(responsibility, "uuid", "__typename"),
            ),
            roleSpecificCriteria: roleCriteria,
            // Only who was added: someone ended elsewhere since isn't put back.
            assignToUsers: item.assignToUsers
              .map(({ id }) => id)
              .filter((id) => !savedHolderIds.includes(id)),
            // A new role has nothing saved to delete yet.
            ...(roleId
              ? {
                  deleteCriteriaIds: uniq(
                    criteriaLists.flatMap((list) => send(list)),
                  ),
                  deleteResponsibilityIds: send(
                    `responsibilities-${item.uuid}`,
                  ),
                  // Only those who hold it on the server: someone added and
                  // taken off before a save never held it (unless a save in
                  // flight makes them, so the rest stay pending).
                  unassignUserIds: send(
                    holdersList,
                    pendingIn(holdersList).filter((id) =>
                      savedHolderIds.includes(id),
                    ),
                  ),
                }
              : {}),
          };
        }),
        // A new track's id, even if this save was set up before the page
        // caught up with the create.
        careerTrackId: careerTrackId ?? createdTrackIdRef.current,
        teamId: associatedTeam?.id,
      },
    });
    if (!response.data?.createOrUpdateCareerTrack?.careerTrack) {
      throw new Error("Career track was not saved");
    }
    sentDeletes.forEach(([list, ids]) => pendingDeletes.remove(list, ids));
    savedTrackEditCountRef.current = trackEditCount;
    // A competency added by hand now applies to this track on the server, so
    // stop sending it: a later save would add the track back to it after the
    // user took it off in the competency modal. Refetched first, so its row
    // stays in the table.
    const sentAdditional = isArray(careerTrackData.additionalCompetencies)
      ? careerTrackData.additionalCompetencies
      : [];
    if (sentAdditional.length > 0) {
      await refetchCompetencies();
      setCareerTrackData((prev) => ({
        ...prev,
        additionalCompetencies: isArray(prev.additionalCompetencies)
          ? prev.additionalCompetencies.filter(
              (id) => !sentAdditional.includes(id),
            )
          : prev.additionalCompetencies,
      }));
    }
  }, [
    isNew,
    fullCompetenciesList,
    associatedTeam,
    careerTrackData,
    careerTrackId,
    roles,
    saveCareerTrack,
    sharedCriteriaEdits,
    saveCompetency,
    refetchCompetencies,
    person,
    personCriteriaEdits,
    savePersonCriteria,
    organizationId,
    pendingDeletes,
  ]);

  const handleAddAdditionalCompetency = useCallback(
    (competencyId: number) => {
      markDirty();
      setCareerTrackData((prev) => ({
        ...prev,
        additionalCompetencies:
          prev.additionalCompetencies && isArray(prev.additionalCompetencies)
            ? [...prev.additionalCompetencies.map(assertNonNull), competencyId]
            : [competencyId],
      }));
    },
    [markDirty],
  );

  // A competency added by hand lives on in additionalCompetencies until the
  // track is saved, and edited shared criteria in sharedCriteriaEdits. If it
  // is deleted in the meantime that id no longer resolves, and every later
  // autosave fails on it, so drop it from both here.
  const handleCompetencyDeleted = useCallback(
    (competencyId: number) => {
      setSharedCriteriaEdits((prev) => omit(prev, competencyId));
      pendingDeletes.clear((list) => list === `shared-${competencyId}`);
      setCareerTrackData((prev) => ({
        ...prev,
        additionalCompetencies:
          prev.additionalCompetencies && isArray(prev.additionalCompetencies)
            ? prev.additionalCompetencies
                .map(assertNonNull)
                .filter((id) => id !== competencyId)
            : prev.additionalCompetencies,
      }));
      refetchCompetencies();
    },
    [refetchCompetencies, pendingDeletes],
  );

  // Deleting a role removes this track's role on that level and nothing else.
  // Levels keep their numbers: they're the ladder shared criteria are written
  // against, so renumbering here would quietly re-pair every role above with
  // another level's criteria (and other tracks' too).
  const handleRemoveLevel = (roleUuid: string) => {
    markDirty();
    // By its key: a role a save in flight is creating gets its id from that
    // save's response.
    pendingDeletes.add("roles", [
      {
        uuid: roleUuid,
        id: roles.find((role) => role.uuid === roleUuid)?.id,
      },
    ]);
    // Its criteria, responsibilities and holders go with it.
    pendingDeletes.clear(
      (list) =>
        list.startsWith(`criteria-${roleUuid}-`) ||
        list === `responsibilities-${roleUuid}` ||
        list === `holders-${roleUuid}`,
    );
    // From what's there now, not what was there when the delete was asked
    // for: a save may have given other roles their ids since.
    setRoles((prev) => prev.filter((role) => role.uuid !== roleUuid));
    // The last role leaves one empty level: the column stays, with no role on
    // it.
    const remaining = roles.filter((role) => role.uuid !== roleUuid);
    setLevelCount(Math.max(1, ...remaining.map((role) => role.level ?? 1)));
  };

  // A level shown in this track that has no role yet: criteria written per
  // role need one, so both the header's + and the empty cells create it.
  const handleAddRoleForLevel = (level: number) => {
    markDirty();
    setRoles((prev) =>
      prev.some((role) => role.level === level)
        ? prev
        : [...prev, createDefaultRole(level)],
    );
    setLevelCount((count) => Math.max(count, level));
  };

  const handleAddLevel = () => {
    markDirty();
    const newLevel = displayLevelCount + 1;
    setLevelCount(newLevel);
    setRoles((prev) =>
      prev.some((role) => role.level === newLevel)
        ? prev
        : [...prev, createDefaultRole(newLevel)],
    );
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollTo({
          left: 1000000,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const [deleteCareerTrack] = useMutation<
    DeleteOrgWideCareerTrackMutation,
    DeleteOrgWideCareerTrackMutationVariables
  >(deleteOrgWideCareerTrackMutation);
  const {
    ConfirmationDialog: DeleteTrackConfirmationDialog,
    confirm: confirmDeleteTrack,
    setMessage: setDeleteTrackMessage,
  } = useConfirm("Delete career track?", "");

  const handleDeleteTrack = async () => {
    if (careerTrackId === null) {
      return;
    }
    const savedRoles = roles.filter((role) => role.id);
    setDeleteTrackMessage(
      `Delete "${careerTrackData.title}" and its ${savedRoles.length} role${
        savedRoles.length === 1 ? "" : "s"
      }, with their criteria and responsibilities? ${describeRoleHolders(
        savedRoles.flatMap((role) => role.assignToUsers),
        savedRoles.length,
      )} ${label("review", { capitalize: true })} questions and answers about these roles are deleted too.`,
    );
    if (!(await confirmDeleteTrack())) {
      return;
    }
    await deleteCareerTrack({ variables: { careerTrackId } });
    successNotificationVar({ title: "Career Track Deleted" });
    // Nothing left to autosave: leave before the unmount flush can recreate it.
    // Flushed so the leave prompt is off before the redirect asks it.
    flushSync(() => {
      setIsFormDirty(false);
      setIsTrackDeleted(true);
    });
    link.redirect(`/settings/organization/${organizationId}/competencies`);
  };

  // Only the first load blanks the page: a refetch after saving keeps what's
  // on screen, so saving doesn't flash the whole editor.
  const loading =
    (isCareerTrackLoading && !data) ||
    (isCompetenciesLoading && !competenciesData);

  // The pinned level header's height, so section title rows can pin just
  // below it (it changes with avatars, "Nobody assigned", etc).
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const thead = theadRef.current;
    if (!thead) {
      return;
    }
    const observer = new ResizeObserver(() =>
      setHeaderHeight(thead.getBoundingClientRect().height),
    );
    observer.observe(thead);
    return () => observer.disconnect();
  }, [loading]);
  // Everything saves as you go, including a brand new track: its first
  // autosave (once it has a name) is what creates it.
  const {
    status: autosaveStatus,
    scheduleSave,
    flushSave,
    cancelSave,
  } = useAutosave({
    save: async () => {
      isTrackSaveInFlightRef.current = true;
      try {
        await handleSaveCareerTrack();
      } finally {
        isTrackSaveInFlightRef.current = false;
      }
    },
    // A new track is created by its first autosave, once it has a name.
    enabled: !!careerTrackData.title?.trim(),
    // Leaving a new track before its first save discards it, as the leave
    // prompt says: that save would create it on the way out.
    saveOnLeave: () => !isNew || createdTrackIdRef.current !== null,
  });
  useEffect(() => {
    if (isFormDirty) {
      scheduleSave();
    }
  }, [
    isFormDirty,
    roles,
    careerTrackData,
    associatedTeam,
    sharedCriteriaEdits,
    personCriteriaEdits,
    levelCount,
    scheduleSave,
  ]);

  const {
    ConfirmationDialog: DiscardTrackConfirmationDialog,
    confirm: confirmDiscardTrack,
  } = useConfirm("Discard career track?", "The changes you made will be lost.");
  // A named new track's autosave may still be waiting: leaving would run it
  // and create the track. So drop it before asking (it would fire while the
  // dialog is open), and turn the leave prompt off, since this asks first.
  const handleDiscardTrack = async () => {
    cancelSave();
    if (isFormDirty && !(await confirmDiscardTrack())) {
      scheduleSave();
      return;
    }
    flushSync(() => {
      setIsFormDirty(false);
      setIsTrackDeleted(true);
    });
    link.redirect(careerTrackOverviewPath(organizationId));
  };

  const competencyLabel = label("competency", { capitalize: true });
  // The picker says what it's adding to, since a competency can be shared.
  const addCompetencyHeader = `Add ${competencyLabel} to ${
    careerTrackData.title?.trim() || "this career track"
  }`;
  // Section titles are the source's own name plus the word: "Design
  // Department Competencies", not "Design Department department competencies".
  const competenciesLabel = label("competency", {
    capitalize: true,
    pluralize: true,
  });

  // Narrowest first: what only this track asks for, what it shares with
  // other tracks, then what every role is held to. A section says who else an
  // edit reaches, so the first one is the only place it stops here.
  const competencySections = useMemo(() => {
    const byScope = groupBy(
      sortBy(fullCompetenciesList, (competency) =>
        competency.title.toLowerCase(),
      ),
      (competency) => getCompetencyScopeSection(competency, careerTrackId),
    );
    const sections: {
      key: CompetencyScopeSection;
      title: string;
      competencies: typeof fullCompetenciesList;
    }[] = [
      {
        key: "track",
        // Named for the track, as the person row is named for the person.
        title: `${careerTrackData.title?.trim() || "This Track"}'s ${competenciesLabel}`,
        competencies: byScope.track ?? [],
      },
      {
        key: "shared",
        title: `Shared ${competenciesLabel}`,
        competencies: byScope.shared ?? [],
      },
      {
        key: "company",
        title: `Company-Wide ${competenciesLabel}`,
        competencies: byScope.company ?? [],
      },
    ];
    // Company-wide and the track's own always show, so the table says what
    // kinds of expectations a role has even before anything is written. The
    // shared one is different: an empty header would suggest a kind of
    // competency that has to be made from another track.
    return sections.filter(
      (section) => section.key !== "shared" || section.competencies.length > 0,
    );
  }, [
    careerTrackData.title,
    careerTrackId,
    competenciesLabel,
    fullCompetenciesList,
  ]);
  // A person's view shows their level alone.
  const visibleLevels = person
    ? [person.level]
    : roleLevelIterator(displayLevelCount);

  const trackMenuOptions: DropdownOptionType[] = [
    // A person's view shows their level alone, so a new level added from
    // there would land on the whole track without appearing anywhere.
    ...(person
      ? []
      : [
          {
            label: "Add Level",
            icon: <PlusIcon className="h-4 w-4" />,
            onClick: handleAddLevel,
          },
        ]),
    {
      // Picking an existing one is what the table's own Add Competency
      // buttons are for; from here you're making a new one.
      label: `Create New ${competencyLabel}`,
      icon: <ViewGridAddIcon className="h-4 w-4" />,
      // A new competency is scoped to this track, which needs an id first.
      // Without one it would be made company-wide.
      disabled: careerTrackId === null,
      description:
        careerTrackId === null
          ? "Available once this track is saved"
          : undefined,
      onClick: () => setCreatingCompetency({ title: "", scope: "track" }),
    },
    {
      label: "Edit Track Details",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: () => setIsEditingDetails(true),
    },
    { type: "divider" },
    {
      label: `Create New ${label("team", { capitalize: true })}`,
      icon: <OfficeBuildingIcon className="h-4 w-4" />,
      onClick: () => setIsCreatingDepartment(true),
    },
    {
      label: "Create New Track",
      icon: <PlusCircleIcon className="h-4 w-4" />,
      onClick: () => link.redirect(newCareerTrackPath(organizationId)),
    },
    // Deleting a whole track is too big a step to take from one person's
    // page; it's on the track's own.
    ...(person
      ? []
      : [
          {
            // A new track isn't saved until it has a name, so there's nothing
            // to delete yet: discarding drops it (and asks first if anything
            // was typed). Not while its first save runs: that creates it.
            label: isNew ? "Discard Track" : "Delete Track",
            icon: <TrashIcon className="h-4 w-4" />,
            textColor: "text-rose-600",
            disabled: isNew && autosaveStatus === "saving",
            onClick: isNew ? handleDiscardTrack : handleDeleteTrack,
          },
        ]),
  ];

  return (
    <div>
      <DeleteTrackConfirmationDialog />
      <DiscardTrackConfirmationDialog />
      {isCreatingDepartment && (
        <TeamEditDialog
          organization={{ id: organizationId }}
          onClose={() => setIsCreatingDepartment(false)}
          // A new department has nothing in it yet, so its own table is the
          // useful place to land.
          onTeamSaved={() => setIsCreatingDepartment(false)}
        />
      )}
      <Prompt
        // A new track isn't saved until it has a name; any track can have a
        // save that failed.
        when={
          !isTrackDeleted &&
          ((isNew && isFormDirty) || autosaveStatus === "error")
        }
        // The first save swaps "new" for the track's own URL, which isn't
        // leaving, even if more was typed while it ran.
        message={(location) =>
          createdTrackIdRef.current !== null &&
          location.pathname ===
            careerTrackPath(organizationId, createdTrackIdRef.current)
            ? true
            : "Are you sure you want to leave? The changes you made will be lost."
        }
      />
      {creatingCompetency !== null && (
        <CompetencyFormModal
          organizationId={organizationId}
          startingData={{
            title: creatingCompetency.title,
          }}
          startingCareerTracks={
            creatingCompetency.scope === "track" && careerTrackId
              ? [
                  {
                    id: careerTrackId,
                    title: careerTrackData.title ?? "",
                  },
                ]
              : undefined
          }
          onClose={() => setCreatingCompetency(null)}
          onCompetencyDeleted={refetchCompetencies}
          onCompetencySaved={(competencyId) => {
            markDirty();
            // Only one made for this track needs adding to it; the others
            // already reach it through the company or the department.
            if (creatingCompetency.scope === "track") {
              handleAddAdditionalCompetency(competencyId);
            }
            setCreatingCompetency(null);
            refetchCompetencies();
          }}
        />
      )}
      <SettingsPageHeader
        breadcrumbs={getRolesAndCompetenciesBreadcrumbs(
          organizationId,
          label,
          person?.name ?? (careerTrackData.title?.trim() || "New Career Track"),
        )}
      >
        {!loading && (data?.careerTrack || isNew || isShowingCreatedTrack) && (
          <AutosaveStatus status={autosaveStatus} onRetry={flushSave} />
        )}
      </SettingsPageHeader>

      {loading && (
        <div className="flex justify-center mt-8 mb-12">
          <Loading />
        </div>
      )}
      {!loading && !data?.careerTrack && !isNew && !isShowingCreatedTrack && (
        <div className="flex justify-center mt-8">
          <div className="text-gray-500">Career Track not found</div>
        </div>
      )}

      {!loading && (data?.careerTrack || isNew || isShowingCreatedTrack) && (
        // Full-height editor: fills the window below the page header (and
        // cancels the settings container's bottom padding), so the toolbar
        // stays put and only the table scrolls.
        <div className="flex flex-col h-[calc(100vh-7rem)] -mb-12">
          {isEditingDetails && (
            <CareerTrackDetailsModal
              organizationId={organizationId}
              title={careerTrackData.title ?? ""}
              team={associatedTeam}
              onClose={() => setIsEditingDetails(false)}
              // As in the Actions menu: not from one person's page.
              onDelete={isNew || person ? undefined : handleDeleteTrack}
              onChange={({ title, team }) => {
                markDirty();
                setCareerTrackData((prev) => ({ ...prev, title }));
                setAssociatedTeam(team);
              }}
            />
          )}
          {/* What's being viewed comes first, then how you're looking at it,
              then what you can do to it. */}
          <div className="flex items-center gap-4 mb-3 shrink-0">
            <CareerTrackViewSwitcher
              organizationId={organizationId}
              careerTrackId={careerTrackId}
              person={person}
              currentTrackTitle={
                person
                  ? undefined
                  : careerTrackData.title?.trim() || "New Career Track"
              }
            />
            {!isNew && (
              <div className="shrink-0">
                <EditModeToggle
                  isEditing={isEditing}
                  onChange={async (next) => {
                    // Leaving edit mode waits for the last save to land.
                    if (!next && !(await flushSave())) {
                      return;
                    }
                    setIsEditing(next);
                  }}
                />
              </div>
            )}
            {/* The track's own actions, beside what's being viewed, only
                while editing. On a person's view too, for their track. */}
            {isEditing && (
              <div className="shrink-0">
                <Dropdown align="start" options={trackMenuOptions}>
                  <Button
                    size={ButtonSize.medium}
                    aria-label="Career track: Actions"
                  >
                    Actions
                    <TopicflowIcon type="horizontalDots" className="h-4 w-4" />
                  </Button>
                </Dropdown>
              </div>
            )}
            {/* Nothing saves until the track has a name, so say so rather
                than let edits look saved. */}
            {isNew && !careerTrackData.title?.trim() && (
              <div className="flex items-center gap-2 min-w-0 text-sm text-gray-500">
                <span className="truncate">
                  Not saved yet. Name this track to save it.
                </span>
                <AddButton
                  icon="edit"
                  size={ButtonSize.medium}
                  text="Name Track"
                  aria-label="Career track: Name track"
                  onClick={() => setIsEditingDetails(true)}
                />
              </div>
            )}
            {/* Flips the table: company-wide first, responsibilities last.
                At the far end of the row. */}
            <Button
              theme={buttonTheme.iconGray}
              icon={TopicflowIconMap.switchVertical01}
              size={ButtonSize.medium}
              className="ml-auto shrink-0"
              aria-label="Career track: Reverse row order"
              aria-pressed={isRowOrderReversed}
              tooltip={
                isRowOrderReversed
                  ? "Show responsibilities first"
                  : `Show company-wide ${label("competency", { pluralize: true })} first`
              }
              onClick={toggleRowOrder}
            />
          </div>
          {roleChooser?.(flushSave)}

          {/* Saving never dims or blocks the table: it happens as you type,
              and the page header is what says "Saving…". */}
          <div className="w-full flex-1 min-h-0 mb-6 rounded-md border overflow-hidden bg-white">
            <div className="relative overflow-auto h-full" ref={tableRef}>
              {/* h-full / min-w-full act as minimums for tables, so short
                  tracks still fill the editor: the responsibilities row takes
                  the extra height and the add-level column the extra width. */}
              <table className="text-sm table-fixed h-full min-w-full">
                <thead ref={theadRef}>
                  <tr>
                    <th
                      className={classNames(
                        stickyColWidth,
                        "px-4 pt-3 border border-l-0 border-t-0 sticky left-0 top-0 z-30 bg-gray-100 text-left align-top text-xs font-medium text-gray-500",
                        stickyHeaderBorderClassName,
                      )}
                    >
                      {/* The same 24px row as each level's "Level N", so the
                          labels line up. */}
                      <div className="flex items-center min-h-[24px]">
                        {`${competenciesLabel} and Responsibilities`}
                      </div>
                      <StickyColumnShadow />
                    </th>
                    {visibleLevels.map((level) => {
                      const role = roles.find((role) => role.level === level);
                      return (
                        <th
                          key={level}
                          className={classNames(
                            role && roleColWidth,
                            !role && emptyRoleColWidth,
                            "group/level px-4 pt-3 pb-5 border border-t-0 text-left font-normal align-top sticky top-0 z-20 bg-gray-100",
                            stickyHeaderBorderClassName,
                          )}
                        >
                          <RoleHeading
                            organizationId={organizationId}
                            readOnly={!isEditing}
                            onBlurSave={flushSave}
                            associatedTeam={associatedTeam}
                            careerTrackData={careerTrackData}
                            level={level}
                            isOnlyRole={roles.length <= 1}
                            role={role}
                            onNewRole={() => handleAddRoleForLevel(level)}
                            onRoleChange={(newRole) => {
                              if (!role) {
                                return;
                              }
                              // Taking a holder off ends their assignment;
                              // putting them back before the save doesn't.
                              // The save sends only those who hold it then.
                              const holders = `holders-${role.uuid}`;
                              // Only someone it holds on the server, or a
                              // save running now is adding: an assignment
                              // this editor never saw isn't ended.
                              const savedHolderIds =
                                (role.id &&
                                  savedHolderIdsRef.current.get(role.id)) ||
                                [];
                              const beingAdded = isTrackSaveInFlightRef.current
                                ? (sentRef.current.roles
                                    .find((sent) => sent.uuid === role.uuid)
                                    ?.assignToUsers.map(({ id }) => id) ?? [])
                                : [];
                              pendingDeletes.add(
                                holders,
                                removedIds(
                                  role.assignToUsers,
                                  newRole.assignToUsers,
                                )
                                  .filter(
                                    (id) =>
                                      savedHolderIds.includes(id) ||
                                      beingAdded.includes(id),
                                  )
                                  .map((id) => ({ id })),
                              );
                              pendingDeletes.remove(
                                holders,
                                newRole.assignToUsers.map(({ id }) => id),
                              );
                              setRoles((prev) =>
                                prev.map((oldRole) => {
                                  if (oldRole.uuid === role.uuid) {
                                    return {
                                      ...oldRole,
                                      ...newRole,
                                    };
                                  }
                                  return oldRole;
                                }),
                              );
                              markDirty();
                            }}
                            onRemoveRole={() => {
                              if (!role) {
                                return;
                              }
                              handleRemoveLevel(role.uuid);
                            }}
                          />
                        </th>
                      );
                    })}
                    <th
                      className={classNames(
                        addLevelColClassName,
                        // The header row draws its own bottom line and has no
                        // top line, like the cells beside it.
                        "border-t-0 px-4 pt-3 pb-5 align-top sticky top-0 z-20 bg-gray-100",
                        stickyHeaderBorderClassName,
                      )}
                    >
                      {isEditing && !person && (
                        <div className="flex">
                          <Button
                            theme={buttonTheme.iconGray}
                            icon={TopicflowIconMap.plus}
                            size={ButtonSize.medium}
                            onClick={handleAddLevel}
                            tooltip="Add level"
                            aria-label="Career track: Add level"
                          />
                        </div>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Narrowest first: this person's own expectations, the
                      role's, then competencies from this track's out to the
                      company's. The order button reads it the other way. */}
                  {orderRows([
                    <Fragment key="person">
                      {/* Expectations for this person alone come first: their view is about them. */}
                      {person && (
                        <>
                          <tr
                            className={classNames(
                              "align-top h-28",
                              bodyRowLineClassName,
                            )}
                            aria-label="Career track: Person criteria row"
                          >
                            <td
                              className={classNames(
                                stickyColWidth,
                                "p-4 border border-l-0 sticky left-0 bg-white z-10",
                                stickyCellBottomLineClassName,
                              )}
                            >
                              <div className="flex items-start gap-2 font-medium">
                                <Avatar user={person} size="5" />
                                {`${person.name}'s Responsibilities`}
                              </div>
                              <StickyColumnShadow />
                            </td>
                            <td
                              className={classNames(
                                roleColWidth,
                                "p-4 border group/cell",
                                personCriteria.length === 0 &&
                                  "align-middle bg-content-background",
                              )}
                            >
                              <CriteriaEditor
                                activeNote={
                                  <CompetencySharingChip
                                    sharing={{
                                      tone: "local",
                                      effect: `Editing here only affects ${person.name}.`,
                                    }}
                                  />
                                }
                                onBlurCriteria={flushSave}
                                revealAddOnHover
                                placeholder="Enter a responsibility"
                                emptyState={{
                                  message: `No responsibilities just for ${person.name}`,
                                  actionText: "Add Responsibility",
                                }}
                                centerEmptyState
                                addButtonText="Add Responsibility"
                                deleteConfirmation={{
                                  title: "Delete responsibility?",
                                  message: `Are you sure you want to delete this responsibility? It's only used for ${person.name}.`,
                                }}
                                criteria={personCriteria}
                                editable={isEditing}
                                onAddCriteria={() => {
                                  markPersonCriteriaDirty();
                                  setPersonCriteriaEdits([
                                    ...personCriteria,
                                    { content: "", uuid: uuidv4() },
                                  ]);
                                }}
                                onPasteMultipleCriteria={(items) => {
                                  markPersonCriteriaDirty();
                                  const updatingUuids = items.map(
                                    ({ uuid }) => uuid,
                                  );
                                  setPersonCriteriaEdits([
                                    ...personCriteria.filter(
                                      (item) =>
                                        !updatingUuids.includes(item.uuid),
                                    ),
                                    ...items.map((item) => ({
                                      ...personCriteria.find(
                                        ({ uuid }) => uuid === item.uuid,
                                      ),
                                      content: item.content,
                                      uuid: item.uuid || uuidv4(),
                                    })),
                                  ]);
                                }}
                                onDeleteCriteria={(uuid) => {
                                  markPersonCriteriaDirty();
                                  pendingDeletes.add("person", [
                                    {
                                      uuid,
                                      id: personCriteria.find(
                                        (item) => item.uuid === uuid,
                                      )?.id,
                                    },
                                  ]);
                                  // From what's there now: a save may have
                                  // given other new ones their ids since.
                                  setPersonCriteriaEdits((prev) =>
                                    (prev ?? personCriteria).filter(
                                      (item) => item.uuid !== uuid,
                                    ),
                                  );
                                }}
                                onCriteriaChange={(uuid, content) => {
                                  markPersonCriteriaDirty();
                                  setPersonCriteriaEdits(
                                    personCriteria.map((item) =>
                                      item.uuid === uuid
                                        ? { ...item, content }
                                        : item,
                                    ),
                                  );
                                }}
                              />
                            </td>
                            <td className={addLevelColClassName} />
                          </tr>
                        </>
                      )}
                    </Fragment>,
                    <Fragment key="responsibilities">
                      <tr
                        className={classNames(
                          "align-top",
                          bodyRowLineClassName,
                          roles.some((role) => role.responsibilities.length > 0)
                            ? "h-52"
                            : "h-28",
                        )}
                        aria-label="Career track: Responsibilities row"
                      >
                        {/* White like a competency's name cell, and named like
                        one: the row has no header of its own. */}
                        <td
                          className={classNames(
                            stickyColWidth,
                            "p-4 border border-l-0 sticky left-0 bg-white z-10",
                            stickyCellBottomLineClassName,
                          )}
                        >
                          <div className="flex items-start gap-2 font-medium">
                            <ScopeSectionIcon
                              scope="track"
                              className="mt-0.5"
                            />
                            Role Responsibilities
                          </div>
                          <StickyColumnShadow />
                        </td>
                        {visibleLevels.map((level) => {
                          const role = roles.find(
                            (role) => role.level === level,
                          );

                          if (!role) {
                            return (
                              <EmptyCriteriaCell
                                key={level}
                                onAddRole={
                                  isEditing
                                    ? () => handleAddRoleForLevel(level)
                                    : undefined
                                }
                              />
                            );
                          }

                          const responsibilities = role.responsibilities ?? [];

                          return (
                            <td
                              className={classNames(
                                roleColWidth,
                                "p-4 border group/cell",
                                responsibilities.length === 0 &&
                                  "align-middle bg-content-background",
                              )}
                              key={level}
                            >
                              <CriteriaEditor
                                activeNote={
                                  <CompetencySharingChip
                                    sharing={{
                                      tone: "local",
                                      effect:
                                        "Editing here only affects this role.",
                                    }}
                                  />
                                }
                                onBlurCriteria={flushSave}
                                revealAddOnHover
                                placeholder="Enter a responsibility"
                                emptyState={{
                                  message: "No responsibilities at this level",
                                  actionText: "Add Responsibility",
                                }}
                                centerEmptyState
                                addButtonText="Add Responsibility"
                                deleteConfirmation={{
                                  title: "Delete responsibility?",
                                  message:
                                    "Are you sure you want to delete this responsibility? It's only used by this role.",
                                }}
                                criteria={responsibilities}
                                editable={isEditing}
                                onAddCriteria={() => {
                                  markDirty();
                                  setRoles((prev) => {
                                    return prev.map((prevRole) => {
                                      if (prevRole.uuid !== role.uuid) {
                                        return prevRole;
                                      }
                                      return {
                                        ...prevRole,
                                        responsibilities: [
                                          ...responsibilities,
                                          { content: "", uuid: uuidv4() },
                                        ],
                                      };
                                    });
                                  });
                                }}
                                onPasteMultipleCriteria={(items) => {
                                  markDirty();
                                  setRoles((prev) => {
                                    return prev.map((prevRole) => {
                                      if (prevRole.uuid !== role.uuid) {
                                        return prevRole;
                                      }
                                      const updatingUuids = items.map(
                                        ({ uuid }) => uuid,
                                      );
                                      return {
                                        ...prevRole,
                                        responsibilities: [
                                          ...responsibilities.filter(
                                            (responsibility) =>
                                              !updatingUuids.includes(
                                                responsibility.uuid,
                                              ),
                                          ),
                                          // Pasted over a saved one: keep it
                                          // (its id), so the save updates it
                                          // rather than replacing it.
                                          ...items.map((item) => ({
                                            ...responsibilities.find(
                                              ({ uuid }) => uuid === item.uuid,
                                            ),
                                            content: item.content,
                                            uuid: item.uuid || uuidv4(),
                                          })),
                                        ],
                                      };
                                    });
                                  });
                                }}
                                onDeleteCriteria={(uuid) => {
                                  markDirty();
                                  pendingDeletes.add(
                                    `responsibilities-${role.uuid}`,
                                    [
                                      {
                                        uuid,
                                        id: responsibilities.find(
                                          (responsibility) =>
                                            responsibility.uuid === uuid,
                                        )?.id,
                                      },
                                    ],
                                  );
                                  setRoles((prev) => {
                                    return prev.map((prevRole) => {
                                      if (prevRole.uuid !== role.uuid) {
                                        return prevRole;
                                      }
                                      return {
                                        ...prevRole,
                                        responsibilities:
                                          prevRole.responsibilities.filter(
                                            (responsibility) =>
                                              responsibility &&
                                              responsibility.uuid !== uuid,
                                          ),
                                      };
                                    });
                                  });
                                }}
                                onCriteriaChange={(uuid, content) => {
                                  markDirty();
                                  setRoles((prev) => {
                                    return prev.map((prevRole) => {
                                      if (prevRole.uuid !== role.uuid) {
                                        return prevRole;
                                      }
                                      return {
                                        ...prevRole,
                                        responsibilities:
                                          prevRole.responsibilities.map(
                                            (responsibility) => {
                                              if (
                                                responsibility.uuid !== uuid
                                              ) {
                                                return responsibility;
                                              }
                                              return {
                                                ...responsibility,
                                                content,
                                              };
                                            },
                                          ),
                                      };
                                    });
                                  });
                                }}
                              />
                            </td>
                          );
                        })}
                        <td className={addLevelColClassName} />
                      </tr>
                    </Fragment>,
                    <Fragment key="competencies">
                      {orderRows(competencySections).map((section) => (
                        <Fragment key={section.key}>
                          {isEditing && (
                            <TitleRow
                              stickyTop={headerHeight}
                              levelCount={displayLevelCount}
                              levels={visibleLevels}
                              title={section.title}
                              toneClassName={sectionHeaderToneClassName}
                              roleLevels={roles.map((role) =>
                                assertNonNull(role.level),
                              )}
                              // Shared ones get no "+": they're made where they're
                              // scoped (a department, or several tracks), not here.
                              action={
                                section.key ===
                                "shared" ? undefined : section.key ===
                                  "track" ? (
                                  <AddCompetencyDropdown
                                    options={otherCompetencyOptions}
                                    header={addCompetencyHeader}
                                    onAddCompetency={
                                      handleAddAdditionalCompetency
                                    }
                                    onCreateCompetency={
                                      careerTrackId === null
                                        ? undefined
                                        : (title) =>
                                            setCreatingCompetency({
                                              title,
                                              scope: "track",
                                            })
                                    }
                                  >
                                    <ComboboxGeneric.Button
                                      theme={buttonTheme.iconGray}
                                      icon={TopicflowIconMap.plus}
                                      size={ButtonSize.medium}
                                      className="-my-1"
                                      tooltip={`Add ${label("competency")}`}
                                      aria-label="Career track: Add competency"
                                    />
                                  </AddCompetencyDropdown>
                                ) : (
                                  <Button
                                    theme={buttonTheme.iconGray}
                                    icon={TopicflowIconMap.plus}
                                    size={ButtonSize.medium}
                                    className="-my-1"
                                    tooltip={`Add ${label("competency")}`}
                                    aria-label={`Career track: Add ${section.key} competency`}
                                    onClick={() =>
                                      setCreatingCompetency({
                                        title: "",
                                        scope: section.key,
                                      })
                                    }
                                  />
                                )
                              }
                            />
                          )}
                          {/* An empty section says so. Shared sections are never
                          empty: they're only shown with something in them. */}
                          {isEditing &&
                            section.key !== "shared" &&
                            section.competencies.length === 0 && (
                              <tr
                                className={bodyRowLineClassName}
                                aria-label={`Career track: Empty ${section.key} section`}
                              >
                                {/* The empty-cell grey, like a level with no
                                criteria. */}
                                <td
                                  className={classNames(
                                    stickyColWidth,
                                    "p-4 border border-l-0 sticky left-0 bg-content-background z-10",
                                    stickyCellBottomLineClassName,
                                  )}
                                >
                                  {/* The header's "+" is the way to add one. */}
                                  <div className="text-gray-400 text-xs">
                                    {section.key === "track"
                                      ? `No ${label("competency", { pluralize: true })} created for this track`
                                      : `No company-wide ${label("competency", { pluralize: true })} created yet`}
                                  </div>
                                  <StickyColumnShadow />
                                </td>
                                {visibleLevels.map((level) => (
                                  <td
                                    key={level}
                                    className={classNames(
                                      roles.some((role) => role.level === level)
                                        ? roleColWidth
                                        : emptyRoleColWidth,
                                      "border bg-content-background",
                                    )}
                                  />
                                ))}
                                <td className={addLevelColClassName} />
                              </tr>
                            )}

                          {section.competencies.map((competency) => (
                            <CareerTrackCompetencyRow
                              key={competency.id}
                              careerTrackId={careerTrackId}
                              onBlurCriteria={flushSave}
                              flushSave={flushSave}
                              sharedCriteria={
                                sharedCriteriaEdits[competency.id] ??
                                assertEdgesNonNull(competency.criteria)
                              }
                              onSharedCriteriaChange={(criteria) => {
                                markDirty();
                                setSharedCriteriaEdits((prev) => ({
                                  ...prev,
                                  [competency.id]: criteria,
                                }));
                              }}
                              onDeleteSharedCriteria={(criteriaUuid) => {
                                markDirty();
                                const saved = assertEdgesNonNull(
                                  competency.criteria,
                                );
                                pendingDeletes.add(`shared-${competency.id}`, [
                                  {
                                    uuid: criteriaUuid,
                                    id: (
                                      sharedCriteriaEdits[competency.id] ??
                                      saved
                                    ).find(
                                      (criteria) =>
                                        criteria.uuid === criteriaUuid,
                                    )?.id,
                                  },
                                ]);
                                // From what's there now: a save may have given
                                // other new ones their ids since.
                                setSharedCriteriaEdits((prev) => ({
                                  ...prev,
                                  [competency.id]: (
                                    prev[competency.id] ?? saved
                                  ).filter(
                                    (criteria) =>
                                      criteria.uuid !== criteriaUuid,
                                  ),
                                }));
                              }}
                              onCompetenciesUpdated={async () => {
                                // The modal saved this competency's criteria: a
                                // list kept from before would overwrite them (or
                                // name criteria it deleted) on the next autosave.
                                setSharedCriteriaEdits((prev) =>
                                  omit(prev, competency.id),
                                );
                                pendingDeletes.clear(
                                  (list) => list === `shared-${competency.id}`,
                                );
                                const refreshed = await refetchCompetencies();
                                const updated = assertEdgesNonNull(
                                  refreshed.data.competencies,
                                ).find(({ id }) => id === competency.id);
                                // Switched between shared and per-role: the
                                // server dropped the other kind's criteria, so
                                // the roles' copies (and their deletes) go too,
                                // or every later autosave would name them.
                                if (
                                  updated &&
                                  updated.criteriaUniqueness !==
                                    competency.criteriaUniqueness
                                ) {
                                  pendingDeletes.clear(
                                    (list) =>
                                      list.startsWith("criteria-") &&
                                      list.endsWith(`-${competency.id}`),
                                  );
                                  setRoles((prev) =>
                                    prev.map((role) => ({
                                      ...role,
                                      criteriaByCompetencyId: omit(
                                        role.criteriaByCompetencyId,
                                        competency.id,
                                      ),
                                    })),
                                  );
                                }
                              }}
                              onCompetencyDeleted={() =>
                                handleCompetencyDeleted(competency.id)
                              }
                              onAddRole={handleAddRoleForLevel}
                              organizationId={organizationId}
                              competency={competency}
                              levelCount={displayLevelCount}
                              levels={visibleLevels}
                              readOnly={!isEditing}
                              roles={roles}
                              onCriteriaChange={(roleUuid, level, items) => {
                                markDirty();
                                setRoles((prev) =>
                                  prev.map((prevRole) => {
                                    if (roleUuid === prevRole.uuid) {
                                      const prevCriteria =
                                        prevRole.criteriaByCompetencyId[
                                          competency.id
                                        ] ?? [];
                                      return {
                                        ...prevRole,
                                        criteriaByCompetencyId: {
                                          ...prevRole.criteriaByCompetencyId,
                                          [competency.id]: [
                                            ...prevCriteria.map((criteria) => {
                                              const newCriteria = items.find(
                                                ({ uuid }) =>
                                                  uuid === criteria.uuid,
                                              );
                                              if (newCriteria) {
                                                return {
                                                  ...criteria,
                                                  content: newCriteria.content,
                                                };
                                              }
                                              return criteria;
                                            }),
                                            ...items
                                              .filter(({ uuid }) => !uuid)
                                              .map((item) => ({
                                                level,
                                                content: item.content,
                                                uuid: uuidv4(),
                                              })),
                                          ],
                                        },
                                      };
                                    }
                                    return prevRole;
                                  }),
                                );
                              }}
                              onDeleteCriteria={(roleUuid, criteriaUuid) => {
                                markDirty();
                                pendingDeletes.add(
                                  `criteria-${roleUuid}-${competency.id}`,
                                  [
                                    {
                                      uuid: criteriaUuid,
                                      id: roles
                                        .find(
                                          (prevRole) =>
                                            prevRole.uuid === roleUuid,
                                        )
                                        ?.criteriaByCompetencyId[
                                          competency.id
                                        ]?.find((criteria) => criteria.uuid === criteriaUuid)
                                        ?.id,
                                    },
                                  ],
                                );
                                setRoles((prev) =>
                                  prev.map((prevRole) => {
                                    if (roleUuid === prevRole.uuid) {
                                      return {
                                        ...prevRole,
                                        criteriaByCompetencyId: {
                                          ...prevRole.criteriaByCompetencyId,
                                          [competency.id]:
                                            prevRole.criteriaByCompetencyId[
                                              competency.id
                                            ]?.filter(
                                              (criteria) =>
                                                criteria.uuid !== criteriaUuid,
                                            ),
                                        },
                                      };
                                    }
                                    return prevRole;
                                  }),
                                );
                              }}
                            />
                          ))}
                        </Fragment>
                      ))}
                    </Fragment>,
                  ])}

                  {/* Takes the table's spare height, so every row keeps its own
                      size instead of the last one stretching to fill the page. */}
                  <tr className="h-full">
                    <td
                      className={classNames(
                        stickyColWidth,
                        "border border-l-0 border-b-0 sticky left-0 z-10 bg-content-background",
                      )}
                    >
                      <StickyColumnShadow />
                    </td>
                    {visibleLevels.map((level) => (
                      <td
                        key={level}
                        className={classNames(
                          roles.some((role) => role.level === level)
                            ? roleColWidth
                            : emptyRoleColWidth,
                          "border border-b-0 bg-content-background",
                        )}
                      />
                    ))}
                    <td
                      className={classNames(addLevelColClassName, "border-b-0")}
                    />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerTrackForm;
